// Copyright (c) 2021, DIALPAD INC., all rights reserved.

import {get, omit} from 'lodash';
import {
    NODE_TYPES,
    TYPE,
    BLOCK_TYPES,
    BLOCK_KEYS,
    CX_BLOCK_TYPES,
    URL,
} from '@app/constants';
import {uuidv4} from '@app/utils/Common';

/**
 * Converts and array of tag strings to an array of objects with the `name` property
 * containing the tag.
 * @param {array} tags
 */
export const formatTags = (tags) => {
    tags = tags || [];
    return tags.map((name) => ({name}));
};

/**
 * Converts and array of tag objects to an array of strings.
 * @param {array} tags
 */
export const normalizeTags = (tags) => {
    tags = tags || [];
    return tags.map((tag) => tag.name);
};

/**
 * Adapts the response form payload:
 * - Removes empty queries
 * - Sets the enabled field to true
 *
 */
// TODO(rupertrutland): Update this method to be in line with new apis.
export const adaptResponsePayload = (response) => {
    const contentKey =
        response[TYPE] === NODE_TYPES.CHATTER
            ? NODE_TYPES.CHATTER
            : NODE_TYPES.RESPONSE;

    const blocks =
        get(response, ['blocks'], [])
            .filter((block) => !!block)
            .map((b) => {
                if (b?.choices_block?.choices?.length) {
                    b.choices_block.choices.forEach((choiceBlock) => {
                        delete choiceBlock.title;
                    });
                }
                return b;
            })
            .filter((b) => {
                if (b && b.choices_block && b.choices_block.choices) {
                    if (b.choices_block.choices.length === 0) {
                        return false;
                    }
                }
                return true;
            }) || [];

    let payload = {
        title: response.title,
        [contentKey]: {
            blocks: blocks,
            queries: response[contentKey].queries
                ? response[contentKey].queries.filter(
                      (query) => query.text !== '',
                  )
                : [],
        },
        status: response.status,
        type: response.type,
        labels: response.labels || [],
    };
    payload = omit(payload, ['archived']);
    payload = omit(payload, ['linked_document']);

    return JSON.stringify(payload);
};

// ids are needed for working nicely with FieldArrays.
// TODO(rr): PAss in uuid function here.
export const getNewBlock = (uuidv1, type) => {
    switch (type) {
        case BLOCK_TYPES.TEXT:
            return {
                [BLOCK_KEYS.TEXT_BLOCK]: {[BLOCK_TYPES.TEXT]: ''},
                [TYPE]: BLOCK_TYPES.TEXT,
                id: uuidv1(),
            };
        case BLOCK_TYPES.QUOTE:
            return {
                [BLOCK_KEYS.QUOTE_BLOCK]: {[BLOCK_TYPES.QUOTE]: ''},
                [TYPE]: BLOCK_TYPES.QUOTE,
                id: uuidv1(),
            };
        case BLOCK_TYPES.WORKFLOW:
            return {
                [BLOCK_KEYS.WORKFLOW_BLOCK]: {[BLOCK_TYPES.WORKFLOW]: ''},
                [TYPE]: BLOCK_TYPES.WORKFLOW,
                id: uuidv1(),
            };
        case BLOCK_TYPES.CHOICES:
            return {
                [BLOCK_KEYS.CHOICES_BLOCK]: {
                    [BLOCK_TYPES.CHOICES]: [],
                },
                [TYPE]: BLOCK_TYPES.CHOICES,
                id: uuidv1(),
            };
        case BLOCK_TYPES.IMAGE:
            return {
                [BLOCK_KEYS.IMAGE_BLOCK]: {
                    [URL]: '',
                },
                [TYPE]: BLOCK_TYPES.IMAGE,
                id: uuidv1(),
            };
        case BLOCK_TYPES.VIDEO:
            return {
                [BLOCK_KEYS.VIDEO_BLOCK]: {
                    [URL]: '',
                },
                [TYPE]: BLOCK_TYPES.VIDEO,
                id: uuidv1(),
            };
        case BLOCK_TYPES.CTA:
            return {
                [BLOCK_KEYS.CTA_BLOCK]: {},
                [TYPE]: BLOCK_TYPES.CTA,
                id: uuidv1(),
            };
        case BLOCK_TYPES.ATTACHMENTS:
            return {
                [BLOCK_KEYS.ATTACHMENT_BLOCK]: {
                    [BLOCK_TYPES.ATTACHMENTS]: [],
                },
                [TYPE]: BLOCK_TYPES.ATTACHMENTS,
                id: uuidv1(),
            };
        case CX_BLOCK_TYPES.NPS:
        case CX_BLOCK_TYPES.CSAT:
        case CX_BLOCK_TYPES.CES:
            return {
                [BLOCK_KEYS.CX_QUESTION_BLOCK]: {},
                [TYPE]: BLOCK_TYPES.CX_QUESTION,
                id: uuidv1(),
            };
    }
};

export const getNewWorkflowBlock = () =>
    getNewBlock(uuidv4, BLOCK_TYPES.WORKFLOW);
export const getNewTextBlock = () => getNewBlock(uuidv4, BLOCK_TYPES.TEXT);
export const getNewImageBlock = () => getNewBlock(uuidv4, BLOCK_TYPES.IMAGE);
export const getNewVideoBlock = () => getNewBlock(uuidv4, BLOCK_TYPES.VIDEO);
export const getNewChoicesBlock = () =>
    getNewBlock(uuidv4, BLOCK_TYPES.CHOICES);
export const getNewQuoteBlock = () => getNewBlock(uuidv4, BLOCK_TYPES.QUOTE);
export const getNewCTABlock = () => getNewBlock(uuidv4, BLOCK_TYPES.CTA);
export const getNewNPSBlock = () => getNewBlock(uuidv4, CX_BLOCK_TYPES.NPS);
export const getNewCSATBlock = () => getNewBlock(uuidv4, CX_BLOCK_TYPES.CSAT);
export const getNewCESBlock = () => getNewBlock(uuidv4, CX_BLOCK_TYPES.CES);
export const getNewAttachmentsBlock = () =>
    getNewBlock(uuidv4, BLOCK_TYPES.ATTACHMENTS);

export const blockUtils = {
    getNewWorkflowBlock,
    getNewTextBlock,
    getNewImageBlock,
    getNewVideoBlock,
    getNewChoicesBlock,
    getNewQuoteBlock,
    getNewCTABlock,
    getNewNPSBlock,
    getNewAttachmentsBlock,
};

export const filterFields = (fields, npsEnabled) => {
    fields.forEach((f, i) => {
        const field = fields.get(i);
        if (
            (field.type === 'choices' &&
                field.choices_block.choices.length === 0) ||
            (field.type === BLOCK_TYPES.CX_QUESTION &&
                field.cx_question_block.type === CX_BLOCK_TYPES.NPS &&
                !npsEnabled)
        ) {
            fields.remove(i);
        }
    });
};
